import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { discoArrayToProductImageStruct, getProductImageAltText } from "helpers/product-image-helpers";
import { PdpImageZoomModalName } from "rtr-constants";
import Header from "./header";
import PDPBreadCrumbs from "../products/pdp-breadcrumbs";
import PDPImagery from "../pdp/pdp-imagery";
import PdpImageZoomModal from "../products/pdp-image-zoom-modal.jsx";
import ReviewModal from "./review-modal";
import ProductDetails from "../products/product-details";
import ProductImageRotation from "helpers/product-image-rotation";
import ProductInfo from "../products/product-info/product-info";
import ReviewThumbNailCarousel from "./review-thumbnail/review-thumbnail-carousel";
import AnchorProductAggregateRating from "components/source/products/anchor-product-aggregate-rating";
import sharedActions from "actions/shared-actions";
import SizePills from "./size-pills/index";
import { fetchAvailability as fetchMembershipAvailability } from "slices/membership-slice";
import RtrButton from "components/design-system/rtr-button";
import SmBagActions from "actions/sm-bag-actions";
import useSailthruConfig from "./useSailthruConfig";
import ProgramEligibilityNotification, { userNotEligibleForProduct } from "./program-eligibility-notification";

export default function PDP() {
  useSailthruConfig();
  const [featureImageSelected, setFeatureImageSelected] = useState(null);
  const [selectedSize, setSelectedSize] = useState(null);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewModalIndex, setReviewModalIndex] = useState(0);
  const isBuyNow = false;

  const addresses = useSelector(state => state.userData?.userProfile?.addresses);
  const staticProduct = useSelector(state => state.staticProduct);
  const isMobileViewport = useSelector(state => state.browser?.isMobileViewport);
  const workingFilters = useSelector(state => state.workingFilters);
  const momentsWithCount = useSelector(state => state.reviewsThumbnailPhotos);
  const gridProductDrawer = useSelector(state => state.gridProductDrawer);
  const reviewsThumbnailPhotos = momentsWithCount?.moments;

  /* TODO: Remove hardcoding of depotId when we figure out filtering mechanism based on location */

  const membershipAvailability = useSelector(state =>
    state.membershipAvailability?.items?.filter(o => o.depotId === "52")
  );
  const membershipAvailabilityLoaded = useSelector(state => state.membershipAvailabilityLoaded);
  const membershipAvailabilityError = useSelector(state => state.membershipAvailabilityError);
  const membershipState = useSelector(state => state.membershipState);

  const userData = useSelector(state => state.userData);

  const smBagLoading = useSelector(state => state.smBag?.smBagLoading);
  const dispatch = useDispatch();

  // FIXME: setting maternity flag to false for now
  const imageRotation = ProductImageRotation(workingFilters?.canonicalSizes || [], staticProduct, false);
  const altText = getProductImageAltText(staticProduct.displayName, staticProduct.designerDisplayName);
  const images = discoArrayToProductImageStruct(staticProduct.images);
  const isUserNotEligibleForProduct = userNotEligibleForProduct(staticProduct, membershipState, userData);

  useEffect(() => {
    dispatch(fetchMembershipAvailability({ productId: staticProduct.productId }));
  }, []);

  const onSubscriptionAddToBag = () => {
    const defaultAddress = addresses?.find(address => address?.default === true);

    const handleAddToMembershipBagSuccess = () => {
      // log the success
    };
    const handleAddToMembershipBagFailure = () => {
      // log the error
    };

    const addSubscriptionItemToBag = SmBagActions.addBagItemPromise(
      {
        sku: selectedSize.sku,
        type: "members",
        zipCode: defaultAddress?.postalCode,
      },
      handleAddToMembershipBagSuccess,
      handleAddToMembershipBagFailure,
      { productId: staticProduct.productId, retailPriceInteger: staticProduct.msrp }
    );
    dispatch(addSubscriptionItemToBag);
  };

  return (
    <div className="pdp-content">
      <div data-test-id="universal_pdp" className="pdp-main-section media-query-layout">
        <PDPImagery
          altText={altText}
          className={"product-images"}
          isCloudZoomEnabled={false}
          images={images}
          imageRotation={imageRotation}
          isMobileViewport={isMobileViewport}
          onClick={(index, ref) => {
            setFeatureImageSelected({ index, ref });
            dispatch(sharedActions.displayModal(PdpImageZoomModalName));
          }}
        />

        <div className="pdp-dynamic-section">
          <Header />
          {/* FIXME: Hardcoding reviews average raiting value */}
          <div className="mt-8 md:mt-6">
            <AnchorProductAggregateRating
              productId={staticProduct.productId}
              reviews={{
                count: momentsWithCount?.totalCount,
                averageRating: 9,
              }}
              redirectUrl={gridProductDrawer?.pdpUrl}
            />
            <ReviewThumbNailCarousel
              setReviewModalOpen={setReviewModalOpen}
              setReviewModalIndex={setReviewModalIndex}
              productId={staticProduct.productId}
            />
          </div>
          <hr className="border-offWhite mt-8"></hr>
          <SizePills
            skus={staticProduct.skus}
            availability={membershipAvailability}
            availabilityLoaded={membershipAvailabilityLoaded}
            availabilityError={membershipAvailabilityError}
            onSelect={setSelectedSize}
          />
          <ProgramEligibilityNotification userNotEligibleForProduct={isUserNotEligibleForProduct} />
          <RtrButton
            style="primary"
            onClick={onSubscriptionAddToBag}
            dataTestId="subscription-atb"
            dataHeapId="subscription-atb"
            className="mt-8 mb-4"
            // when implementing intents ensure -> !membershipState?.inventoryEligibilities?.length > 0 is added to loading state check to verify if membershipState inventory eligibilites has loaded
            loading={smBagLoading}
            disabled={!selectedSize || isUserNotEligibleForProduct}
            fullWidth>
            {isUserNotEligibleForProduct ? "Unavailable" : "Add to Bag"}
          </RtrButton>
        </div>

        <PdpImageZoomModal
          altText={altText}
          imageRotation={imageRotation}
          images={images}
          isMobileViewport={isMobileViewport}
          openingElement={featureImageSelected}
        />
      </div>

      <ProductInfo
        selected={null}
        showOptionalDetails={isBuyNow}
        isMobileViewport={isMobileViewport}
        imageRotation={imageRotation}
        images={images}
        displayName={staticProduct.displayName}
        designerDisplayName={staticProduct.designerDisplayName}
        productId={staticProduct.productId}
        fitNotes={staticProduct.fitNotes}
        stylistNotes={staticProduct.stylistNotes}
        productDetails={staticProduct.productDetails}
      />

      {!isMobileViewport && (
        <ProductDetails
          selected={null}
          showOptionalDetails={isBuyNow}
          isMobileViewport={isMobileViewport}
          imageRotation={imageRotation}
          images={images}
          displayName={staticProduct.displayName}
          designerDisplayName={staticProduct.designerDisplayName}
          productId={staticProduct.productId}
          fitNotes={staticProduct.fitNotes}
          stylistNotes={staticProduct.stylistNotes}
          productDetails={staticProduct.productDetails}
        />
      )}
      <PDPBreadCrumbs />
      {reviewsThumbnailPhotos?.length > 0 && reviewModalOpen && (
        <ReviewModal
          productDisplayName={staticProduct?.displayName}
          productId={staticProduct?.productId}
          ind={reviewModalIndex}
          setIsOpen={setReviewModalOpen}
          isOpen={reviewModalOpen}
        />
      )}
    </div>
  );
}
